'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Edit2, Trash2, MapPin, Phone, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';

// Backend integration: GET /api/assets?type=person
const people = [
  { id: 'per-001', techId: 'TECH-01', name: 'Travis Reyes', role: 'Lead Driller', certifications: ['Well Control', 'H2S'], status: 'en-route', gpsStatus: 'strong', assignedJob: 'FJ-2841', lastLocation: 'Hwy 90, mm 42', phone: '432-555-0192', shiftStart: '06:00', hoursToday: 7.9 },
  { id: 'per-002', techId: 'TECH-02', name: 'Maria Gutierrez', role: 'Equipment Operator', certifications: ['Crane Op', 'OSHA 30'], status: 'on-site', gpsStatus: 'strong', assignedJob: 'FJ-2838', lastLocation: 'Block 14, Andrews TX', phone: '432-555-0247', shiftStart: '07:00', hoursToday: 6.9 },
  { id: 'per-003', techId: 'TECH-03', name: 'Kyle Odom', role: 'Field Technician', certifications: ['H2S', 'OSHA 10'], status: 'offline', gpsStatus: 'lost', assignedJob: undefined, lastLocation: 'FM 1788, Midland (last known)', phone: '432-555-0311', shiftStart: '08:00', hoursToday: 5.6 },
  { id: 'per-004', techId: 'TECH-04', name: 'Aaron Thibodeau', role: 'Rig Supervisor', certifications: ['Well Control', 'BOSIET', 'H2S'], status: 'on-site', gpsStatus: 'strong', assignedJob: 'FJ-2825', lastLocation: 'Pad 12, Loving County', phone: '432-555-0488', shiftStart: '05:30', hoursToday: 8.4 },
  { id: 'per-005', techId: 'TECH-05', name: 'Darnell Brooks', role: 'Driver / Operator', certifications: ['CDL-A', 'Hazmat'], status: 'returning', gpsStatus: 'strong', assignedJob: 'FJ-2833', lastLocation: 'US-285 S', phone: '432-555-0552', shiftStart: '06:30', hoursToday: 7.4 },
  { id: 'per-006', techId: 'TECH-06', name: 'Sofia Castillo', role: 'Field Technician', certifications: ['OSHA 30', 'H2S'], status: 'en-route', gpsStatus: 'strong', assignedJob: 'FJ-2829', lastLocation: 'US-385 N', phone: '432-555-0614', shiftStart: '07:30', hoursToday: 6.4 },
  { id: 'per-007', techId: 'TECH-07', name: 'James Whitfield', role: 'Equipment Operator', certifications: ['Crane Op', 'CDL-B'], status: 'available', gpsStatus: 'strong', assignedJob: undefined, lastLocation: 'Yard, Midland TX', phone: '432-555-0733', shiftStart: '08:00', hoursToday: 5.9 },
  { id: 'per-008', techId: 'TECH-08', name: 'Priya Nair', role: 'Safety Officer', certifications: ['OSHA 30', 'First Aid', 'H2S'], status: 'en-route', gpsStatus: 'strong', assignedJob: 'FJ-2841', lastLocation: 'Hwy 90, mm 42', phone: '432-555-0821', shiftStart: '07:00', hoursToday: 6.9 },
  { id: 'per-009', techId: 'TECH-09', name: 'Caleb Sheridan', role: 'Wireline Specialist', certifications: ['Well Control', 'Wireline Ops'], status: 'available', gpsStatus: 'strong', assignedJob: undefined, lastLocation: 'Yard, Midland TX', phone: '432-555-0944', shiftStart: '09:00', hoursToday: 4.9 },
  { id: 'per-010', techId: 'TECH-10', name: 'Renee Holbrook', role: 'Field Inspector', certifications: ['API 510', 'OSHA 30'], status: 'returning', gpsStatus: 'strong', assignedJob: 'FJ-2833', lastLocation: 'US-285 S', phone: '432-555-1012', shiftStart: '07:00', hoursToday: 6.9 },
  { id: 'per-011', techId: 'TECH-11', name: 'Marco Reyes', role: 'Pump Operator', certifications: ['H2S', 'OSHA 10'], status: 'on-site', gpsStatus: 'strong', assignedJob: 'FJ-2836', lastLocation: 'Pad 7, Pecos TX', phone: '432-555-1122', shiftStart: '06:00', hoursToday: 7.9 },
  { id: 'per-012', techId: 'TECH-12', name: 'Sandra Nguyen', role: 'Rig Supervisor', certifications: ['Well Control', 'BOSIET', 'H2S', 'OSHA 30'], status: 'on-site', gpsStatus: 'strong', assignedJob: 'FJ-2825', lastLocation: 'Pad 12, Loving County', phone: '432-555-1234', shiftStart: '05:00', hoursToday: 8.9 },
];

export default function PeopleTable() {
  const [search, setSearch] = useState('');
  const filtered = people.filter((p) => {
    const q = search.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || p.role.toLowerCase().includes(q) || p.techId.toLowerCase().includes(q);
  });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
        <input
          type="text"
          placeholder="Search personnel..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-all w-56"
        />
        <span className="text-xs text-zinc-600 ml-auto">{filtered.length} personnel</span>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b border-zinc-800">
              {['Tech ID', 'Name / Role', 'Certifications', 'Status', 'GPS', 'Assigned Job', 'Location', 'Shift Start', 'Hrs Today', 'Contact', 'Actions'].map((h) => (
                <th key={`ppl-h-${h}`} className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {filtered.map((p) => (
              <tr key={p.id} className="group hover:bg-zinc-800/40 transition-colors">
                <td className="px-4 py-3 font-mono text-xs font-bold text-amber-400">{p.techId}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-zinc-700 border border-zinc-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-zinc-300">{p.name.split(' ').map((n) => n[0]).join('')}</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-zinc-200">{p.name}</p>
                      <p className="text-[10px] text-zinc-500">{p.role}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {p.certifications.slice(0, 2).map((cert) => (
                      <span key={`cert-${p.id}-${cert}`} className="text-[9px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-400">{cert}</span>
                    ))}
                    {p.certifications.length > 2 && (
                      <span className="text-[9px] text-zinc-600">+{p.certifications.length - 2}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status as any} /></td>
                <td className="px-4 py-3">
                  {p.gpsStatus === 'lost'
                    ? <span className="flex items-center gap-1 text-[11px] text-red-400"><WifiOff size={11} /> Lost</span>
                    : <span className="flex items-center gap-1 text-[11px] text-emerald-400"><Wifi size={11} /> Strong</span>}
                </td>
                <td className="px-4 py-3">
                  {p.assignedJob
                    ? <span className="font-mono text-xs text-amber-400">{p.assignedJob}</span>
                    : <span className="text-xs text-zinc-600 italic">Unassigned</span>}
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-zinc-400 flex items-start gap-1 max-w-[130px]">
                    <MapPin size={9} className="mt-0.5 flex-shrink-0" /><span className="truncate">{p.lastLocation}</span>
                  </p>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-zinc-400">{p.shiftStart}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-mono font-semibold tabular-nums ${p.hoursToday >= 8 ? 'text-amber-400' : 'text-zinc-300'}`}>{p.hoursToday}h</span>
                </td>
                <td className="px-4 py-3">
                  <a href={`tel:${p.phone}`} className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-sky-400 transition-colors">
                    <Phone size={10} /> {p.phone}
                  </a>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => toast.info(`Editing ${p.name}`)} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 transition-all"><Edit2 size={13} /></button>
                    <button onClick={() => toast.error(`${p.name} removed from roster`)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}